/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: ProtectedComponent
 * Reason: Tiny presentational summary card for a single LogicFlowTrace header.
 *         Renders status badge, timing, mode/level chips, step counts and
 *         optional error message. No business logic — display only. Layout is
 *         100% PrimeFlex + PrimeNG, the .scss file stays empty.
 *
 * @ticket TM-1000001563
 *   Status badge now consumes BE-provided enum metadata
 *   (statusEnumName / statusEnumImage / statusEnumColor /
 *   statusEnumColorBackground). The boolean-derived `successLabel`,
 *   `successSeverity`, `successIcon` getters were removed — their
 *   template bindings are gone.
 */
import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {LogicFlowTraceDto} from '../../../model/dto/entity/logic-flow-trace.dto';
import {LogicFlowTraceLevelEnum} from '../../../enum/logic-flow-trace-level.enum';
import {LogicFlowExecutionModeEnum} from '../../../enum/logic-flow-execution-mode.enum';
import {MvsObjectNavigationService} from '../../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from '../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from '../../../../../core/shared/navigation/mvs-object-navigation-action-enum';

@Component({
    selector: 'mvs-lg-flow-trace-summary',
    templateUrl: './lg-flow-trace-summary.component.html',
    styleUrls: ['./lg-flow-trace-summary.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LgFlowTraceSummaryComponent {

    @Input() trace: LogicFlowTraceDto | null = null;

    constructor(private navigationService: MvsObjectNavigationService) {
    }

    /**
     * Short label shown on the caller chip — combines the polymorphic
     * ObjectType alias with the caller id. Returns an empty string when no
     * caller is resolvable; the template suppresses the whole row in that
     * case via an @if guard.
     */
    get callerLabel(): string {
        if (!this.trace?.callerObjectTypeDtoName || this.trace?.callerObjectId == null) {
            return '';
        }
        return `${this.trace.callerObjectTypeDtoName} #${this.trace.callerObjectId}`;
    }

    /**
     * Opens the caller entity's detail page in the right drawer using the
     * standard polymorphic navigation pattern. No-op when the caller fields
     * are not populated (e.g. designer runs or test-case traces).
     */
    navigateToCaller(): void {
        if (!this.trace?.callerObjectTypeDtoName || this.trace?.callerObjectId == null) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            this.trace.callerObjectTypeDtoName,
            this.trace.callerObjectId,
            null,
            'Object',
            null,
            null,
            MvsObjectNavigationActionEnum.any
        );
        this.navigationService.navigateTo(entry, 'right');
    }

    /**
     * German label for the trace level chip. Mirrors the dropdown labels on
     * the flow config form (Aus / Zähler / Vollständig).
     */
    get traceLevelLabel(): string {
        if (!this.trace || this.trace.traceLevel == null) {
            return '–';
        }
        switch (this.trace.traceLevel) {
            case LogicFlowTraceLevelEnum.NONE:
                return 'Aus';
            case LogicFlowTraceLevelEnum.COUNTER:
                return 'Zähler';
            case LogicFlowTraceLevelEnum.FULL:
                return 'Vollständig';
            default:
                return '–';
        }
    }

    /**
     * German label for the execution mode chip.
     */
    get executionModeLabel(): string {
        if (!this.trace || this.trace.executionMode == null) {
            return '–';
        }
        switch (this.trace.executionMode) {
            case LogicFlowExecutionModeEnum.NORMAL:
                return 'Normal';
            case LogicFlowExecutionModeEnum.DEBUG:
                return 'Debug';
            case LogicFlowExecutionModeEnum.DRY_RUN:
                return 'Probelauf';
            default:
                return '–';
        }
    }

    /**
     * Format a duration in milliseconds as a short human-readable string.
     * < 1000 ms  → '<n> ms'
     * < 60 000   → '<n.n> s'
     * otherwise  → '<n> min <n> s'
     */
    get formattedDuration(): string {
        const ms = this.trace?.totalDurationMs;
        if (ms == null) {
            return '–';
        }
        if (ms < 1000) {
            return `${ms} ms`;
        }
        if (ms < 60_000) {
            return `${(ms / 1000).toFixed(1)} s`;
        }
        const minutes = Math.floor(ms / 60_000);
        const seconds = Math.floor((ms % 60_000) / 1000);
        return `${minutes} min ${seconds} s`;
    }
}
